<template>
  <div class="theme-portal h-screen flex flex-col overflow-hidden bg-app" :class="{ 'layout-modern': isRxHarmony }" :style="brandStyle" @keydown.escape="isSidebarOpen = false">
    <div class="w-full bg-primary-100 flex justify-between items-center text-primary-600 py-0.5 px-4 text-sm" v-if="isAuthenticatedInPinMode">
      <div />
      <div>You are in pin mode as {{ userName }}</div>
      <base-button size="xs" type="secondary" @click="enablePinMode">
        Switch User
        <template #icon>
          <sign-out-icon />
        </template>
      </base-button>
    </div>
    <header class="relative z-20 flex h-16 shrink-0 items-center bg-topnav" :style="glowStyle">
      <button @click.stop="isSidebarOpen = true" class="px-4 text-topnav-ink/70 focus:outline-none md:hidden" v-if="!isOrganizationUser">
        <menu-icon class="h-6 w-6" />
      </button>
      <div class="flex w-[238px] shrink-0 items-center px-6 max-md:w-auto">
        <img v-if="isRxHarmony" src="/v2/logo-inline-lumen.png" class="h-[26px] w-auto" alt="RxHarmony" />
        <component :is="logo" class="h-[26px] w-auto" v-else />
      </div>
      <div class="v2-header-search min-w-0 flex-1">
        <global-search v-if="isOrganizationBusiness" :business-id="organization.id" class="hidden md:block" />
      </div>
      <div class="flex items-center gap-3 px-4 sm:px-5">
        <base-dropdown :options="profileOptions" align="right" :show-background="false">
          <div class="flex cursor-pointer items-center rounded-md px-2 py-1 transition-colors hover:bg-white/10">
            <div class="flex h-[34px] w-[34px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-resolve">
              <img v-if="isRxHarmony" src="/v2/brandmark-lumen.png" class="h-[22px] w-[22px] object-contain" alt="" />
              <avatar
                v-else
                :business-id="isOrganizationBusiness ? organization.id : null"
                :user-id="isOrganizationBusiness ? null : userId"
                :avatar-type="isOrganizationBusiness ? 'business' : 'user'"
              />
            </div>
            <div class="ml-3 hidden leading-4 sm:block">
              <div class="text-sm font-semibold text-topnav-ink">{{ cleanUserName }}</div>
              <div class="text-xs text-topnav-ink/55">{{ organization.description }}</div>
            </div>
            <chevron-down-icon class="ml-2 h-5 w-5 text-topnav-ink/55" />
          </div>
          <template #option="{ option }">
            <div class="my-1 w-full border-b" v-if="option.isDivider" />
            <button class="flex items-center px-4 py-2 text-gray-600 focus:outline-none" @click="option.onClick()" v-else>
              <component :is="option.icon" class="mr-2.5 h-5 w-5 shrink-0" />
              <div class="text-left">
                <div :class="[{ 'text-sm leading-4': option.description }]">{{ option.title }}</div>
                <div class="text-xs text-gray-500" v-if="option.description">{{ option.description }}</div>
              </div>
            </button>
          </template>
        </base-dropdown>
      </div>
    </header>

    <div class="flex flex-1 overflow-hidden">
      <transition leave-active-class="ease-in duration-200">
        <div v-show="isSidebarOpen" class="md:hidden" @click="isSidebarOpen = false">
          <div class="fixed inset-0 z-30 bg-zen/50" />
          <div class="fixed inset-0 z-40 flex">
            <div class="flex w-full max-w-xs flex-col bg-sidebar">
              <div class="flex h-16 shrink-0 items-center justify-between bg-topnav px-4" :style="glowStyle">
                <img v-if="isRxHarmony" src="/v2/logo-inline-lumen.png" class="h-[26px] w-auto" alt="RxHarmony" />
                <component :is="logo" class="h-[26px] w-auto" v-else />
                <button class="text-topnav-ink" @click="isSidebarOpen = false"><x-icon class="h-6 w-6" /></button>
              </div>
              <nav class="flex-1 overflow-y-auto py-3">
                <router-link v-for="navItem in navItems" :key="navItem.path" :to="navItem.path" :class="navRowClass(navItem)">
                  <component :is="navIcon(navItem)" class="h-[19px] w-[19px] shrink-0" />
                  <span>{{ navItem.text }}</span>
                  <base-badge v-if="showTodoBadge(navItem)" :dark="true" color="primary" size="xs">{{ todoCount }}</base-badge>
                </router-link>
              </nav>
            </div>
          </div>
        </div>
      </transition>

      <aside class="relative hidden w-[238px] shrink-0 flex-col overflow-hidden border-r border-sidebar-line bg-sidebar md:flex">
        <nav class="relative z-10 shrink-0 py-3">
          <router-link v-for="navItem in navItems" :key="navItem.path" :to="navItem.path" :class="navRowClass(navItem)">
            <component :is="navIcon(navItem)" class="h-[19px] w-[19px] shrink-0" />
            <span>{{ navItem.text }}</span>
            <base-badge v-if="showTodoBadge(navItem)" :dark="true" color="primary" size="xs">{{ todoCount }}</base-badge>
          </router-link>
        </nav>
        <div class="flex flex-1 min-h-0 flex-col items-center overflow-hidden pt-[100px] pointer-events-none" aria-hidden="true">
          <svg width="97" height="185" viewBox="-27 -6 289 552" fill="none" class="shrink-0" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M116.6,7.2 L116.6,307.3 C116.6,315.2 123.8,321.6 132.6,321.6 L243,321.6 C251.8,321.6 259,328 259,335.9 L259,426.4 C259,434.3 251.8,440.7 243,440.7 L16,440.7 C7.2,440.7 0,447.1 0,455 L0,506.7 C0,514.6 7.2,521 16,521 L101.5,521 C110.3,521 117.5,527.4 117.5,535.3 L117.5,867.1"
              stroke="#20413D"
              stroke-width="6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M87.8,48.2 C89.2,49.1 91.1,48.7 91.9,47.3 L116.6,8.6 L141.3,47.3 C142.2,48.7 144,49.1 145.4,48.2 C146.8,47.3 147.2,45.4 146.3,44.1 L119.1,1.4 C118.5,0.5 117.6,0 116.6,0 C115.6,0 114.6,0.5 114.1,1.4 L86.9,44.1 C86.6,44.6 86.4,45.2 86.4,45.7 C86.4,46.7 86.9,47.7 87.8,48.2 Z"
              fill="#20413D"
            />
          </svg>

          <div class="flex-1 w-[2px] bg-[#20413D]"></div>
        </div>
      </aside>

      <div class="flex w-0 flex-1 flex-col overflow-hidden">
        <div v-if="showActivationHeader" class="flex justify-between gap-2 border-b border-t bg-red-50 px-4 py-6 text-base text-red-600 sm:px-6 lg:px-8">
          <div class="flex-col">
            <div>Not Active</div>
            <div v-if="numRemainingActivationSteps > 3" class="text-xs">
              Let's get started! Complete {{ numRemainingActivationSteps }} more steps to activate your business and get your {{ organization.customerLabelPlural }} signing up.
            </div>
            <div v-else class="text-xs">
              You're almost there! Only {{ numRemainingActivationSteps }} more steps to activate your business and get your {{ organization.customerLabelPlural }} signing up.
            </div>
          </div>
          <base-button class="my-auto flex" type="primary" size="sm" @click="activation">
            <template v-slot:icon>
              <task-list-icon />
            </template>
            Checklist
          </base-button>
        </div>

        <main :class="['relative flex-1 overflow-y-auto bg-app focus:outline-none', { 'pb-12': isOrganizationUser }]" tabindex="0">
          <slot />
        </main>

        <nav class="absolute inset-x-0 bottom-0 flex border-t border-sidebar-line bg-white md:hidden" v-if="isOrganizationUser">
          <router-link
            v-for="navItem in navItems"
            :key="navItem.path"
            :to="navItem.path"
            :class="[
              'relative flex flex-1 flex-col items-center px-0 py-2 text-sm font-normal transition-colors duration-100 ease-out hover:no-underline',
              isActivePath(navItem) ? 'text-resolve hover:text-resolve' : 'text-sidenav-ink/60 hover:text-sidenav-ink',
            ]"
          >
            <div v-if="isActivePath(navItem)" class="absolute bottom-0 h-1 w-20 rounded-t-md bg-pulse" />
            <component :is="navIcon(navItem)" class="mx-auto h-[19px] w-[19px]" />
            {{ navItem.shortText || navItem.text }}
          </router-link>
        </nav>
      </div>
    </div>

    <notification-wrapper />
    <global-error />
  </div>
</template>

<script>
import navigation from "@/mixins/navigation";
import brandThemeStyle from "@/components/v2/brandTheme";
import { typographyStyle } from "@/components/v2/portalTheme";
import NavDashboardIcon from "@/assets/svgs/v2/nav-dashboard-icon.svg";
import NavPatientsIcon from "@/assets/svgs/v2/nav-patients-icon.svg";
import NavPrescriptionsIcon from "@/assets/svgs/v2/nav-prescriptions-icon.svg";
import NavActivitiesIcon from "@/assets/svgs/v2/nav-activities-icon.svg";
import NavTrainingIcon from "@/assets/svgs/v2/nav-training-icon.svg";
import NavPlansIcon from "@/assets/svgs/v2/nav-plans-icon.svg";
import NavReportsIcon from "@/assets/svgs/v2/nav-reports-icon.svg";
import NavSettingsIcon from "@/assets/svgs/v2/nav-settings-icon.svg";
import NavSupportIcon from "@/assets/svgs/v2/nav-support-icon.svg";

const V2_NAV_ICONS = {
  "graph-icon": "nav-dashboard-icon", // Dashboard
  "users-icon": "nav-patients-icon", // Patients / Members
  "pill-icon": "nav-prescriptions-icon", // Prescriptions
  "check-icon": "nav-activities-icon", // Activities
  "training-icon": "nav-training-icon", // Training
  "table-icon": "nav-plans-icon", // Plans & Products
  "report-icon": "nav-reports-icon", // Reports
  "settings-icon": "nav-settings-icon", // Settings
  "support-icon": "nav-support-icon", // Support
};

export default {
  mixins: [navigation],
  components: {
    NavDashboardIcon,
    NavPatientsIcon,
    NavPrescriptionsIcon,
    NavActivitiesIcon,
    NavTrainingIcon,
    NavPlansIcon,
    NavReportsIcon,
    NavSettingsIcon,
    NavSupportIcon,
  },
  computed: {
    brandStyle() {
      const theme = this.$store.getters["organization/portalTheme"];
      if (!theme) return {};
      return Object.assign(brandThemeStyle({ primary: theme.accentColor, secondary: theme.activeNavColor }), typographyStyle(theme));
    },
    isRxHarmony() {
      return this.cobrandId === "rx-harmony";
    },
    glowStyle() {
      return {
        height: "64px",
        flex: "0 0 64px",
        background: this.isRxHarmony
          ? "linear-gradient(rgba(32, 65, 61, 0.05), rgba(32, 65, 61, 0.05)), url('/v2/glow-pulse.png') -250px -460px / 620px 620px no-repeat, var(--color-topnav)"
          : "var(--color-topnav)",
        display: "flex",
        gap: "16px",
        padding: " 0 22px 0 0",
        alignItems: "center",
      };
    },
  },
  created() {
    this.$store.dispatch("organization/loadPortalTheme");
  },
  methods: {
    navIcon(navItem) {
      return V2_NAV_ICONS[navItem.icon] || navItem.icon;
    },
    navRowClass(navItem) {
      return [
        "flex items-center gap-[13px] border-l-4 px-[22px] py-3 text-[15px] transition-colors duration-100 hover:no-underline",
        this.isActivePath(navItem)
          ? "border-pulse bg-white font-semibold text-resolve shadow-[0_1px_5px_rgba(32,65,61,0.12)]"
          : "border-transparent text-sidenav-ink/80 hover:bg-white/50",
      ];
    },
    showTodoBadge(navItem) {
      return navItem.text === "Activities" && this.isLocationFiltered === true && this.todoCount > 0;
    },
  },
};
</script>
